import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Zap, ArrowLeft, ArrowRight, Building2, User, Mail, Lock, Check, Loader2, X, CheckCircle2, RefreshCw, Clock } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { API_URL, BACKEND_URL } from "../config/api";
import { TIMEZONES, getTimezonesForCountry, DEFAULT_TIMEZONE, DEFAULT_TIMEZONE_USA } from "../utils/timezones";

const PLATFORM_API = `${API_URL}/platform`;

const SignupV3 = () => {
  const [step, setStep] = useState(1); // 1 = contact info, 2 = verify, 3 = password, 4 = company, 5 = success
  const [loading, setLoading] = useState(false);
  const [checkingShortname, setCheckingShortname] = useState(false);
  const [shortnameAvailable, setShortnameAvailable] = useState(null);
  const [shortnameSuggestions, setShortnameSuggestions] = useState([]);
  const [timeLeft, setTimeLeft] = useState(300); // 5 minutes countdown
  const navigate = useNavigate();

  // Step 1: Contact info
  const [ownerName, setOwnerName] = useState("");
  const [ownerEmail, setOwnerEmail] = useState("");
  const [ownerPhone, setOwnerPhone] = useState("");
  const [countryCode, setCountryCode] = useState("+1"); // Add country code state

  // Step 2: Verification
  const [pin, setPin] = useState(["", "", "", "", "", ""]);
  const inputRefs = useRef([]);

  // Step 3: Security
  const [adminPassword, setAdminPassword] = useState("");

  // Step 4: Company details
  const [companyName, setCompanyName] = useState("");
  const [shortname, setShortname] = useState("");
  const [country, setCountry] = useState("USA");
  const [timezone, setTimezone] = useState(DEFAULT_TIMEZONE_USA);
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  // Success info
  const [createdOrg, setCreatedOrg] = useState(null);

  // Countdown timer for verification
  useEffect(() => {
    if (step !== 2 || timeLeft <= 0) return;

    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          toast.error("Verification code expired. Please request a new one.");
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [step, timeLeft]);

  // Format time as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Auto-generate shortname from company name
  const generateShortname = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '') // Remove special chars
      .replace(/\s+/g, '') // Remove spaces
      .substring(0, 20); // Max 20 chars
  };

  // Check shortname availability with backend
  const checkShortname = async (shortname) => {
    if (!shortname || shortname.length < 3) {
      setShortnameAvailable(null);
      setShortnameSuggestions([]);
      return;
    }

    setCheckingShortname(true);
    try {
      const response = await axios.get(`${API_URL}/auth/check-slug/${shortname}`);
      
      if (response.data.available) {
        setShortnameAvailable(true);
        setShortnameSuggestions([]);
      } else {
        setShortnameAvailable(false);
        setShortnameSuggestions(response.data.suggestions || []);
      }
    } catch (error) {
      console.error('Error checking slug:', error);
      // On error, assume available (fail-open for better UX)
      setShortnameAvailable(true);
      setShortnameSuggestions([]);
    } finally {
      setCheckingShortname(false);
    }
  };

  // Handle company name change
  const handleCompanyNameChange = (e) => {
    const name = e.target.value.slice(0, 100); // Enforce max length
    setCompanyName(name);
    
    // Auto-generate shortname
    if (name) {
      const generated = generateShortname(name);
      setShortname(generated);
      setShortnameAvailable(null); // Reset to null during typing
      setCheckingShortname(false);
    } else {
      setShortname("");
      setShortnameAvailable(null);
    }
  };

  // Handle manual shortname change with debounce
  const handleShortnameChange = (e) => {
    const value = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '').slice(0, 50);
    setShortname(value);
    setShortnameAvailable(null); // Reset to null while typing
    setShortnameSuggestions([]);
  };
  
  // Debounced availability check - only check after user stops typing
  useEffect(() => {
    if (!shortname || shortname.length < 3) {
      setShortnameAvailable(null);
      setShortnameSuggestions([]);
      setCheckingShortname(false);
      return;
    }
    
    // Set checking state immediately
    setCheckingShortname(true);
    
    // Debounce: Wait 800ms after user stops typing
    const debounceTimer = setTimeout(() => {
      checkShortname(shortname);
    }, 800);
    
    return () => clearTimeout(debounceTimer);
  }, [shortname]);

  // Validate step 1
  const validateStep1 = () => {
    if (!companyName.trim()) {
      toast.error("Please enter your company name");
      return false;
    }
    if (!shortname.trim() || shortname.length < 3) {
      toast.error("Shortname must be at least 3 characters");
      return false;
    }
    // Backend will validate uniqueness at signup time
    return true;
  };

  // Validate step 2
  const validateStep2 = () => {
    if (!ownerName.trim()) {
      toast.error("Please enter the owner name");
      return false;
    }
    if (!ownerEmail.trim() || !/\S+@\S+\.\S+/.test(ownerEmail)) {
      toast.error("Please enter a valid email address");
      return false;
    }
    // Phone is optional, but if provided, validate format
    if (ownerPhone.trim()) {
      const phoneClean = ownerPhone.replace(/[\s\-\(\)\.\+]/g, '');
      if (phoneClean.length < 10 || !/^\d+$/.test(phoneClean)) {
        toast.error("Please enter a valid phone number with at least 10 digits");
        return false;
      }
    }
    if (!adminPassword || adminPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return false;
    }
    if (adminPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return false;
    }
    if (!agreedToTerms) {
      toast.error("Please agree to the Terms of Service and Privacy Policy");
      return false;
    }
    return true;
  };

  // Handle step 1 continue
  const handleStep1Continue = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  // Handle final signup
  const handleSignup = async (e) => {
    e.preventDefault();
    
    // Validate phone is required
    if (!ownerPhone.trim()) {
      toast.error('Phone number is required');
      return;
    }
    
    if (!agreedToTerms) {
      toast.error('Please agree to the Terms and Privacy Policy');
      return;
    }

    setLoading(true);
    try {
      // Prepare phone number - combine country code + digits
      const phoneDigits = ownerPhone.replace(/[^0-9]/g, '');
      const formattedPhone = `${countryCode}${phoneDigits}`;

      // Use the correct public signup endpoint
      const response = await axios.post(`${API_URL}/auth/signup`, {
        organizationName: companyName,
        adminName: ownerName,
        adminEmail: ownerEmail,
        adminPassword: adminPassword,
        adminPhone: formattedPhone,
        timezone: timezone,
        country: country
      });

      if (response.data) {
        toast.success("Account created successfully!");
        
        // Go directly to pending approval (email already verified in Step 2)
        navigate('/pending-approval', {
          state: {
            organizationName: response.data.organizationName,
            organizationSlug: response.data.slug,
            email: ownerEmail
          }
        });
      }
    } catch (error) {
      console.error("Signup error:", error);
      if (error.response?.data?.detail) {
        toast.error(error.response.data.detail);
      } else {
        toast.error("Failed to create account. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gray-50">
      <Card className="w-full max-w-md shadow-xl relative overflow-hidden rounded-t-2xl rounded-b-none">
        {/* Back button */}
        {step < 5 && (
          <div className="absolute top-4 left-4 z-10">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                if (step === 1) {
                  navigate('/');
                } else {
                  setStep(step - 1);
                }
              }}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back
            </Button>
          </div>
        )}

        <CardHeader className="text-center pt-16 pb-6">
          <div className="flex justify-center mb-4">
            <img 
              src="/logo-icon.png" 
              alt="BookingFlows" 
              className="w-14 h-14 rounded-xl object-cover"
            />
          </div>
          <CardTitle className="text-3xl font-bold" style={{color: '#1a1a1a'}}>
            Booking<span className="text-blue-600">Flows</span>
          </CardTitle>
          <p className="text-gray-600 mt-2">
            {step === 1 && "Your Contact Information"}
            {step === 2 && "Verify Your Email"}
            {step === 3 && "Create Password"}
            {step === 4 && "Company Details"}
            {step === 5 && "Welcome!"}
          </p>
          
          {/* Progress Indicator */}
          {step < 5 && (
            <div className="flex items-center justify-center gap-2 mt-6 py-2">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                1
              </div>
              <div className={`w-12 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} />
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                2
              </div>
              <div className={`w-12 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} />
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                3
              </div>
              <div className={`w-12 h-1 ${step >= 4 ? 'bg-blue-600' : 'bg-gray-200'}`} />
              <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${step >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}>
                4
              </div>
            </div>
          )}
        </CardHeader>

        <CardContent className="px-8 pb-8">
          {/* Step 1: Contact Info - Name, Email, Phone */}
          {step === 1 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="ownerName" className="text-sm font-semibold text-gray-700">
                  Your Name <span className="text-red-600">*</span>
                </Label>
                <Input
                  id="ownerName"
                  placeholder="e.g., John Smith"
                  value={ownerName}
                  onChange={(e) => setOwnerName(e.target.value)}
                  className="mt-2 h-12 text-base"
                  maxLength={100}
                  required
                />
              </div>

              <div>
                <Label htmlFor="ownerEmail" className="text-sm font-semibold text-gray-700">
                  Email Address <span className="text-red-600">*</span>
                </Label>
                <Input
                  id="ownerEmail"
                  type="email"
                  placeholder="your@email.com"
                  value={ownerEmail}
                  onChange={(e) => setOwnerEmail(e.target.value)}
                  className="mt-2 h-12 text-base"
                  maxLength={255}
                  required
                />
              </div>

              <div>
                <Label htmlFor="ownerPhone" className="text-sm font-semibold text-gray-700">
                  Phone Number <span className="text-red-600">*</span>
                </Label>
                <div className="flex gap-2 mt-2">
                  <select
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="h-12 px-3 rounded-md border border-gray-300 bg-white text-sm font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
                    style={{ width: '120px', flexShrink: 0 }}
                    required
                  >
                    <option value="+1">🇺🇸 +1</option>
                    <option value="+971">🇦🇪 +971</option>
                  </select>
                  <Input
                    id="ownerPhone"
                    type="tel"
                    inputMode="numeric"
                    placeholder="555-123-4567"
                    value={ownerPhone}
                    onChange={(e) => {
                      const cleaned = e.target.value.replace(/[^0-9\s\-\(\)]/g, '');
                      setOwnerPhone(cleaned);
                    }}
                    className="h-12 text-base flex-1"
                    maxLength={25}
                    required
                  />
                </div>
              </div>

              <Button
                onClick={async () => {
                  if (!ownerName.trim()) {
                    toast.error('Please enter your name');
                    return;
                  }
                  if (!ownerEmail.trim() || !ownerEmail.includes('@')) {
                    toast.error('Please enter a valid email');
                    return;
                  }
                  if (!ownerPhone.trim()) {
                    toast.error('Please enter your phone number');
                    return;
                  }
                  
                  // Send verification email
                  setLoading(true);
                  try {
                    await axios.post(`${API_URL}/auth/send-verification-pin`, {
                      email: ownerEmail,
                      name: ownerName
                    });
                    toast.success('Verification code sent!');
                    setTimeLeft(300); // Reset timer to 5 minutes
                    setStep(2);
                  } catch (error) {
                    if (error.response?.data?.detail) {
                      toast.error(error.response.data.detail);
                    } else {
                      toast.error('Failed to send code');
                    }
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading || !ownerName.trim() || !ownerEmail.trim() || !ownerPhone.trim()}
                className="w-full h-12 text-base font-semibold mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
                className="!bg-blue-600 hover:!bg-blue-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Continue <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Step 2: Verify Email PIN */}
          {step === 2 && (
            <div className="space-y-4">
              <div className="text-center mb-6">
                <div className="flex justify-center mb-4">
                  <div className="p-3 rounded-xl bg-blue-100">
                    <Mail className="w-8 h-8 text-blue-600" />
                  </div>
                </div>
                <p className="text-sm text-gray-600">
                  We sent a 6-digit code to
                </p>
                <p className="text-sm font-semibold text-gray-900 mt-1">{ownerEmail}</p>
              </div>

              {/* Countdown Timer */}
              <div className={`flex items-center justify-center gap-2 mb-6 p-3 rounded-lg ${
                timeLeft < 60 ? 'bg-red-50 text-red-600' : 'bg-blue-50 text-blue-600'
              }`}>
                <Clock className="w-5 h-5" />
                <span className="font-mono text-lg font-bold">
                  {formatTime(timeLeft)}
                </span>
                <span className="text-sm">remaining</span>
              </div>

              {/* PIN Input */}
              <div className="flex justify-center gap-2 mb-6">
                {pin.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => {
                      const value = e.target.value.replace(/\D/g, '');
                      if (!value) return;
                      
                      const newPin = [...pin];
                      newPin[index] = value;
                      setPin(newPin);
                      
                      if (index < 5) {
                        inputRefs.current[index + 1]?.focus();
                      } else {
                        // Last digit entered - auto-submit
                        const fullPin = [...newPin].join('');
                        if (fullPin.length === 6) {
                          setTimeout(async () => {
                            setLoading(true);
                            try {
                              const response = await axios.post(`${API_URL}/auth/verify-pin`, {
                                email: ownerEmail,
                                pin: fullPin
                              });
                              
                              if (response.data.success) {
                                toast.success('Email verified!');
                                setStep(3);
                              } else {
                                toast.error('Invalid code');
                                setPin(["", "", "", "", "", ""]);
                              }
                            } catch (error) {
                              toast.error('Invalid code');
                              setPin(["", "", "", "", "", ""]);
                            } finally {
                              setLoading(false);
                            }
                          }, 100);
                        }
                      }
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Backspace' && !pin[index] && index > 0) {
                        inputRefs.current[index - 1]?.focus();
                      }
                    }}
                    onPaste={(e) => {
                      e.preventDefault();
                      const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
                      if (pastedData.length === 6) {
                        setPin(pastedData.split(''));
                        inputRefs.current[5]?.focus();
                      }
                    }}
                    className="w-10 h-12 text-center text-xl font-bold border-2 border-gray-300 rounded-lg focus:border-blue-600 focus:ring-2 focus:ring-blue-600 focus:outline-none"
                  />
                ))}
              </div>

              <div className="text-center mb-4">
                <p className="text-xs text-gray-600 mb-2">
                  Didn't receive the code?
                </p>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={async () => {
                    try {
                      await axios.post(`${API_URL}/auth/send-verification-pin`, {
                        email: ownerEmail,
                        name: ownerName
                      });
                      toast.success('New code sent!');
                      setPin(["", "", "", "", "", ""]);
                      setTimeLeft(300); // Reset timer
                    } catch (error) {
                      toast.error('Failed to resend');
                    }
                  }}
                  className="text-blue-600 hover:text-blue-700 text-xs"
                >
                  <RefreshCw className="w-3 h-3 mr-1" />
                  Resend
                </Button>
              </div>

              <Button
                onClick={async () => {
                  const pinValue = pin.join('');
                  if (pinValue.length !== 6) {
                    toast.error('Please enter all 6 digits');
                    return;
                  }
                  
                  setLoading(true);
                  try {
                    const response = await axios.post(`${API_URL}/auth/verify-pin`, {
                      email: ownerEmail,
                      pin: pinValue
                    });
                    
                    if (response.data.success) {
                      toast.success('Email verified!');
                      setStep(3);
                    } else {
                      toast.error('Invalid code');
                      setPin(["", "", "", "", "", ""]);
                    }
                  } catch (error) {
                    toast.error('Invalid code');
                    setPin(["", "", "", "", "", ""]);
                  } finally {
                    setLoading(false);
                  }
                }}
                disabled={loading || pin.join('').length !== 6}
                className="w-full h-12 text-base font-semibold disabled:opacity-50"
                className="!bg-blue-600 hover:!bg-blue-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    Verify <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Step 3: Password */}
          {step === 3 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="adminPassword" className="text-sm font-semibold text-gray-700">
                  Create Password <span className="text-red-600">*</span>
                </Label>
                <Input
                  id="adminPassword"
                  type="password"
                  placeholder="Create a strong password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  className="mt-2 h-12 text-base"
                  maxLength={128}
                  required
                />
              </div>

              {/* Password Requirements Checklist */}
              <div className="bg-gray-50 rounded-lg p-4 space-y-2">
                <p className="text-xs font-semibold text-gray-700 mb-3">Password Requirements:</p>
                
                {/* At least 8 characters */}
                <div className="flex items-center gap-2">
                  {adminPassword.length >= 8 ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <X className="w-4 h-4 text-red-500" />
                  )}
                  <span className={`text-xs ${adminPassword.length >= 8 ? 'text-green-700' : 'text-gray-600'}`}>
                    At least 8 characters
                  </span>
                </div>

                {/* Lowercase letter */}
                <div className="flex items-center gap-2">
                  {/[a-z]/.test(adminPassword) ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <X className="w-4 h-4 text-red-500" />
                  )}
                  <span className={`text-xs ${/[a-z]/.test(adminPassword) ? 'text-green-700' : 'text-gray-600'}`}>
                    1 lowercase letter
                  </span>
                </div>

                {/* Uppercase letter */}
                <div className="flex items-center gap-2">
                  {/[A-Z]/.test(adminPassword) ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <X className="w-4 h-4 text-red-500" />
                  )}
                  <span className={`text-xs ${/[A-Z]/.test(adminPassword) ? 'text-green-700' : 'text-gray-600'}`}>
                    1 uppercase letter
                  </span>
                </div>

                {/* Number */}
                <div className="flex items-center gap-2">
                  {/[0-9]/.test(adminPassword) ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <X className="w-4 h-4 text-red-500" />
                  )}
                  <span className={`text-xs ${/[0-9]/.test(adminPassword) ? 'text-green-700' : 'text-gray-600'}`}>
                    1 number
                  </span>
                </div>

                {/* Symbol */}
                <div className="flex items-center gap-2">
                  {/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(adminPassword) ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <X className="w-4 h-4 text-red-500" />
                  )}
                  <span className={`text-xs ${/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(adminPassword) ? 'text-green-700' : 'text-gray-600'}`}>
                    1 symbol (!@#$%^&*)
                  </span>
                </div>
              </div>

              <Button
                onClick={() => {
                  const isValid = 
                    adminPassword.length >= 8 &&
                    /[a-z]/.test(adminPassword) &&
                    /[A-Z]/.test(adminPassword) &&
                    /[0-9]/.test(adminPassword) &&
                    /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(adminPassword);
                  
                  if (!isValid) {
                    toast.error('Please meet all password requirements');
                    return;
                  }
                  setStep(4);
                }}
                disabled={
                  adminPassword.length < 8 ||
                  !/[a-z]/.test(adminPassword) ||
                  !/[A-Z]/.test(adminPassword) ||
                  !/[0-9]/.test(adminPassword) ||
                  !/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(adminPassword)
                }
                className="w-full h-12 text-base font-semibold mt-6 disabled:opacity-50"
                className="!bg-blue-600 hover:!bg-blue-700"
              >
                Continue <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          )}

          {/* Step 4: Company Details (After Verification) */}
          {step === 4 && (
            <div className="space-y-4">
              <div>
                <Label htmlFor="companyName" className="text-sm font-semibold text-gray-700">
                  Company Name <span className="text-red-600">*</span>
                </Label>
                <Input
                  id="companyName"
                  placeholder="e.g., Austin Rental Boats"
                  value={companyName}
                  onChange={handleCompanyNameChange}
                  className="mt-2 h-12 text-base"
                  maxLength={100}
                  required
                />
              </div>

              <div>
                <Label htmlFor="shortname" className="text-sm font-semibold text-gray-700">
                  Shortname <span className="text-red-600">*</span>
                </Label>
                <div className="relative">
                  <Input
                    id="shortname"
                    placeholder="e.g., austinboats"
                    value={shortname}
                    onChange={handleShortnameChange}
                    className="mt-2 h-12 text-base pr-12"
                    maxLength={50}
                    required
                  />
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {checkingShortname && shortname.length >= 3 && (
                      <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                    )}
                    {!checkingShortname && shortnameAvailable === true && shortname.length >= 3 && (
                      <Check className="w-5 h-5 text-green-600" />
                    )}
                    {!checkingShortname && shortnameAvailable === false && (
                      <X className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                </div>
                {!checkingShortname && shortnameAvailable === false && shortnameSuggestions.length > 0 && (
                  <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-xs text-red-700 mb-2">Try these:</p>
                    <div className="flex flex-wrap gap-2">
                      {shortnameSuggestions.map((suggestion, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => setShortname(suggestion)}
                          className="px-2 py-1 bg-white border border-red-300 rounded text-xs text-red-700 hover:bg-red-50"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="country" className="text-sm font-semibold text-gray-700">
                  Country <span className="text-red-600">*</span>
                </Label>
                <select
                  id="country"
                  value={country}
                  onChange={(e) => {
                    setCountry(e.target.value);
                    // Auto-set timezone based on country using centralized config
                    if (e.target.value === 'UAE') {
                      setTimezone(DEFAULT_TIMEZONE);
                    } else if (e.target.value === 'USA') {
                      setTimezone(DEFAULT_TIMEZONE_USA);
                    }
                  }}
                  className="mt-2 w-full h-12 px-4 border rounded-lg text-base"
                  required
                >
                  <option value="USA">🇺🇸 United States (USD)</option>
                  <option value="UAE">🇦🇪 United Arab Emirates (AED)</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  This determines your currency and Stripe Connect requirements. Cannot be changed later.
                </p>
              </div>

              <div>
                <Label htmlFor="timezone" className="text-sm font-semibold text-gray-700">
                  Timezone <span className="text-red-600">*</span>
                </Label>
                <select
                  id="timezone"
                  value={timezone}
                  onChange={(e) => setTimezone(e.target.value)}
                  className="mt-2 w-full h-12 px-4 border rounded-lg text-base"
                  required
                >
                  {/* Show country-specific timezones first, then all timezones */}
                  <optgroup label={country === 'UAE' ? '🇦🇪 UAE Timezones' : '🇺🇸 US Timezones'}>
                    {getTimezonesForCountry(country).map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </optgroup>
                  <optgroup label="All Timezones">
                    {TIMEZONES.filter(tz => !getTimezonesForCountry(country).some(ctz => ctz.value === tz.value)).map((tz) => (
                      <option key={tz.value} value={tz.value}>
                        {tz.label}
                      </option>
                    ))}
                  </optgroup>
                </select>
              </div>

              <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <input
                  type="checkbox"
                  id="terms-checkbox"
                  checked={agreedToTerms}
                  onChange={(e) => setAgreedToTerms(e.target.checked)}
                  className="w-4 h-4 text-blue-600 rounded flex-shrink-0"
                />
                <label htmlFor="terms-checkbox" className="text-xs text-gray-700">
                  I agree to the{' '}
                  <a href="/terms" target="_blank" className="text-blue-600 hover:underline font-semibold">
                    Terms
                  </a>
                  {' '}and{' '}
                  <a href="/privacy" target="_blank" className="text-blue-600 hover:underline font-semibold">
                    Privacy Policy
                  </a>
                </label>
              </div>

              <Button
                onClick={handleSignup}
                disabled={loading || !agreedToTerms || !companyName.trim() || !shortname.trim() || shortnameAvailable !== true}
                className="w-full h-12 text-base font-semibold mt-6"
                className="!bg-blue-600 hover:!bg-blue-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    Create Account <ArrowRight className="ml-2 w-5 h-5" />
                  </>
                )}
              </Button>
            </div>
          )}

          {/* Step 5: Success */}
          {step === 5 && createdOrg && (
            <div className="space-y-6">
              <div>
                <Label htmlFor="companyName" className="text-sm font-semibold text-gray-700">
                  Company Name *
                </Label>
                <Input
                  id="companyName"
                  placeholder="e.g., Austin Rental Assets"
                  value={companyName}
                  onChange={handleCompanyNameChange}
                  className="mt-2 h-12 text-base"
                />
              </div>

              <div>
                <Label htmlFor="shortname" className="text-sm font-semibold text-gray-700">
                  Shortname *
                </Label>
                <div className="relative">
                  <Input
                    id="shortname"
                    placeholder="e.g., austinrentalboats"
                    value={shortname}
                    onChange={handleShortnameChange}
                    className="mt-2 h-12 text-base pr-12"
                  />
                  {/* Inline status indicator */}
                  <div className="absolute right-3 top-1/2 -translate-y-1/2">
                    {checkingShortname && shortname.length >= 3 && (
                      <Loader2 className="w-5 h-5 text-gray-400 animate-spin" />
                    )}
                    {!checkingShortname && shortnameAvailable === true && shortname.length >= 3 && (
                      <Check className="w-5 h-5 text-green-600" />
                    )}
                    {!checkingShortname && shortnameAvailable === false && (
                      <X className="w-5 h-5 text-red-600" />
                    )}
                  </div>
                </div>
                
                {/* Suggestions below input (only if taken) */}
                {!checkingShortname && shortnameAvailable === false && shortnameSuggestions.length > 0 && (
                  <div className="mt-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-xs text-red-700 mb-2 font-semibold">'{shortname}' is not available. Try these:</p>
                    <div className="flex flex-wrap gap-2">
                      {shortnameSuggestions.map((suggestion, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => {
                            setShortname(suggestion);
                          }}
                          className="px-3 py-1 bg-white border border-red-300 rounded-md text-sm text-red-700 hover:bg-red-50 transition-colors"
                        >
                          {suggestion}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              <Button
                onClick={handleStep1Continue}
                disabled={!companyName.trim() || !shortname.trim() || shortname.length < 3 || shortnameAvailable !== true || checkingShortname}
                className="w-full !bg-blue-600 hover:!bg-blue-700 text-white h-12 text-base font-semibold mt-6 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </div>
          )}

          {/* Step 5: Success */}
          {step === 5 && createdOrg && (
            <div className="text-center py-8">
              <div className="bg-amber-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Check className="w-10 h-10 text-amber-600" />
              </div>
              
              <h3 className="text-2xl font-bold mb-4">Account Created Successfully!</h3>
              <p className="text-gray-600 mb-4">
                Thank you for signing up for BookingFlows!
              </p>

              <div className="bg-amber-50 border-2 border-amber-300 rounded-lg p-6 mb-6">
                <p className="text-amber-900 font-semibold mb-2">⏳ Pending Approval</p>
                <p className="text-sm text-amber-800">
                  Your account is currently pending approval. Our team will review your application and reach out within 24 hours to help you get set up.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6 text-left">
                <p className="text-sm font-semibold text-gray-700 mb-3">Your Login Details (for when approved):</p>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-600">Organization Shortname</p>
                    <p className="text-base font-mono font-bold">{createdOrg.slug}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Username (Your Email)</p>
                    <p className="text-base font-mono font-bold">{ownerEmail}</p>
                  </div>
                  <div>
                    <p className="text-xs text-gray-600">Login URL</p>
                    <p className="text-sm text-blue-600">
                      {window.location.origin}/login
                    </p>
                  </div>
                </div>
              </div>

              <p className="text-sm text-gray-600 mb-6">
                💡 Save these details! You'll need them once your account is approved.
              </p>

              <p className="text-xs text-gray-500 mb-6">
                Questions? Email us at support@bookingflows.zendesk.com
              </p>

              <Button
                onClick={() => navigate('/')}
                className="w-full !bg-blue-600 hover:!bg-blue-700 text-white h-12 text-base font-semibold"
              >
                Back to Home
              </Button>
            </div>
          )}
        </CardContent>
        
        {/* Footer - matches login page exactly */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 text-center">
          {step < 5 && (
            <p className="text-xs text-gray-500">
              Already have an account?{' '}
              <a
                href="/login"
                className="font-semibold hover:underline"
                className="text-blue-600"
              >
                Sign in here
              </a>
            </p>
          )}
          <p className="text-xs text-gray-400 mt-2">
            © 2025 BookingFlows. All rights reserved.
          </p>
        </div>
      </Card>
    </div>
  );
};

export default SignupV3;
